import { CreditCostMarker } from "@/components/features/Credits/CreditCostMarker";
import { canonicalUrl } from "@/config/canonical";
import { useSeoCopy } from "@/config/seo";
import { HeroSection } from "@/containers/HeroSection";
import { Button } from "@/design-system/Button/Button";
import Card from "@/design-system/Card/Card";
import { CompletedChip } from "@/design-system/CompletedChip/CompletedChip";
import { CustomizeButton } from "@/design-system/CustomizeButton/CustomizeButton";
import { Skeleton } from "@/design-system/Skeleton/Skeleton";
import { TextButton } from "@/design-system/TextButton/TextButton";
import { useStartClubImport } from "@/features/MyClub/EmptyClub/useStartClubImport";
import { useGoBack } from "@/hooks/useGoBack";
import { cn } from "@/lib/utils";
import { globalStateSelector } from "@/redux/globalState/globalStateSlice";
import { showModal } from "@/redux/message/messageSlice";
import { AppDispatch } from "@/redux/store";
import {
  getUserSelector,
  userIsLoggedInSelector,
} from "@/redux/user/userSlice";
import { BREAKPOINTS } from "@/settings";
import { safeGetJSON, safeSetItem } from "@/utils/safeStorage";
import {
  GALLERY_STORAGE_KEYS,
  firstOwnersMinutes as firstOwnersMinutesFor,
  grouped,
} from "@repo/shared/gallery";
import {
  useCollectedSync,
  useGalleryLevelPlan,
  useGalleryLevelPlanner,
  useGalleryPlanLimits,
} from "@repo/shared/hooks";
import { useTranslation } from "@repo/shared/i18n";
import { useCreditGate } from "@repo/shared/redux";
import { ArrowDown, BookOpen } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { NavLink, useLocation } from "react-router";
import { useMediaQuery } from "usehooks-ts";

import { GalleryLevelPlanDetail } from "./components/GalleryLevelPlanDetail";
import { GalleryLevelPlanSummary } from "./components/GalleryLevelPlanSummary";
import { GalleryLevelPlanner } from "./components/GalleryLevelPlanner";
import { GalleryLevelSettingsPanel } from "./components/GalleryLevelSettingsPanel";
import {
  GalleryLevelsGuideCard,
  GalleryLevelsGuideSheet,
} from "./components/GalleryLevelsGuide";
import { GallerySteps } from "./components/GallerySteps";
import {
  galleryStepsSync,
  useCollectedPlayersSync,
} from "./components/useCollectedPlayersSync";

const CLUB_IMPORT_ORIGIN = { section: "Gallery", subsection: "Levels" };
/** `/fut-gallery/levels`: build a level plan for a Gallery level and see its totals. */
export const GalleryLevelsPage = () => {
  const { t, i18n } = useTranslation("gallery");
  const seo = useSeoCopy("galleryLevels");
  const goBack = useGoBack({ fallback: "/fut-gallery" });
  const { user } = useSelector(getUserSelector);
  const isLoggedIn = useSelector(userIsLoggedInSelector);
  // Signed out, playerCount is undefined rather than 0.
  const clubImported = user.playerCount > 0;
  const dispatch = useDispatch<AppDispatch>();
  const location = useLocation();
  const startClubImport = useStartClubImport(CLUB_IMPORT_ORIGIN);
  const creditGate = useCreditGate("gallery-level-plan-built");
  const [synced, setSynced] = useState(false);
  const sync = useCollectedPlayersSync(() => setSynced(true));
  // Step 1's sync state, read again once a sync here completes.
  const { collectedSync } = useCollectedSync(user.uuid, synced);
  const { lastImportFinishedAt } = useSelector(globalStateSelector);
  // The coins from the latest Club Import, shown in Coin balance like on the Gallery Planner; read again after an import.
  const { coins } = useGalleryPlanLimits(user.uuid, lastImportFinishedAt);
  const levels = useGalleryLevelPlan(isLoggedIn ? user.uuid : null);
  const {
    milestones,
    plan,
    setCount,
    status,
    building,
    error,
    refresh,
    setItems,
    loadSetItems,
    bought,
    toggleBought,
    toggleSetDone,
  } = levels;
  const planner = useGalleryLevelPlanner({ levels, coins, creditGate });
  const { level, limitFields, planReady, planTarget } = planner;
  // A club import's Done answers once the extension has sent the objectives, so the milestones' completed states read again.
  const importFinishedAtMount = useRef(lastImportFinishedAt);
  useEffect(() => {
    if (lastImportFinishedAt !== importFinishedAtMount.current) refresh();
  }, [lastImportFinishedAt, refresh]);
  const stepsSync = galleryStepsSync(
    sync,
    collectedSync,
    synced,
    i18n.language,
  );
  const firstOwnersMinutes = firstOwnersMinutesFor(
    collectedSync?.collectedCount,
  );
  // Open until the first plan, so a first visit starts in it; then folded until Edit plan.
  const [plannerOpen, setPlannerOpen] = useState<boolean>();
  // Beside Build Plan: the failed build's error.
  const buildStatus =
    error === "CLUB_IMPORT_REQUIRED" ? (
      <span className="text-small text-amber-400">
        {t("levels.importRequired")}
      </span>
    ) : error ? (
      <span className="text-small text-amber-400">{t("levels.error")}</span>
    ) : undefined;
  const [guideOpen, setGuideOpen] = useState(false);
  // The link pulses until the guide is first opened on this device.
  const [guideSeen, setGuideSeen] = useState(
    () => safeGetJSON<boolean>(GALLERY_STORAGE_KEYS.levelsGuideOpen) !== null,
  );
  const openGuide = () => {
    setGuideOpen(true);
    setGuideSeen(true);
    safeSetItem(GALLERY_STORAGE_KEYS.levelsGuideOpen, "true");
  };
  // Desktop: in the page header, as it covers the whole page; touch devices: its own row under the nav.
  const isTouchDevice = useMediaQuery(BREAKPOINTS.TOUCH_DEVICE.query);
  const isMobile = useMediaQuery(BREAKPOINTS.MOBILE.query);
  const guideLink = (
    <TextButton
      size="medium"
      onClick={openGuide}
      icon={<BookOpen />}
      className={cn(
        "text-blue-400 hover:text-blue-300",
        !guideSeen && "animate-pulse",
      )}
    >
      {t("levels.guide.title")}
    </TextButton>
  );
  const plannerRef = useRef<HTMLDivElement>(null);
  const openPlanner = () => {
    setPlannerOpen(true);
    plannerRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  const ready = isLoggedIn && status !== "pending" && status !== "rejected";

  return (
    <main className="z-10 h-full min-h-screen flex-1 bg-background text-white">
      <HeroSection
        helmetTitle={seo.title}
        helmetDescription={seo.description}
        pageTitle={seo.pageTitle}
        subtitle={seo.subtitle}
        helmetCanonicalLink={canonicalUrl("/fut-gallery/levels")}
        back={goBack}
        // Inset by the content's gutter, so it lines up with the content's right edge.
        headerAction={
          plan && <span className="pr-4 large-desktop:pr-8">{guideLink}</span>
        }
        shouldBeInTheDocumentFlow
      />
      {/* Touch devices have no page header: the guide link gets its own row under the nav. */}
      {isTouchDevice && ready && plan && (
        <div className="mx-auto flex w-full max-w-content justify-end px-4 pt-4">
          {guideLink}
        </div>
      )}
      <div className="mx-auto flex w-full max-w-content flex-col gap-y-10 px-4 pb-8 pt-6 large-desktop:px-8">
        {/* Signed out too: Import Club in the steps takes a new user through sign-in. */}
        {status === "pending" ? (
          <Card hoverable={false} className="flex flex-col gap-y-4 p-4">
            <Skeleton className="h-8 w-44 rounded-full" />
            <Skeleton className="h-16 w-full rounded-xl" />
          </Card>
        ) : status === "rejected" ? (
          <p className="text-center text-sm text-foreground-muted">
            {t("levels.loadError")}
          </p>
        ) : (
          <>
            <GallerySteps
              clubImported={clubImported}
              lastImportedAt={user.lastImportedAt}
              onImportClub={() =>
                void startClubImport({ returnTo: location.pathname })
              }
              sync={stepsSync}
              lastStep={{
                title: t("levels.steps.plan.title"),
                description: t("levels.steps.plan.description"),
                built: !!plan,
                done: false,
                // Opens the planner; Build Plan, which costs a Credit, sits at its foot.
                footer: (
                  <span className="flex min-w-0 flex-1 flex-wrap items-center gap-x-3 gap-y-1.5">
                    {/* A link down to the planner, not an action: Build Plan sits at its foot. */}
                    <TextButton
                      size="medium"
                      disabled={!clubImported}
                      onClick={openPlanner}
                      className="text-blue-400 hover:text-blue-300"
                    >
                      {plan
                        ? t("levels.steps.plan.edit")
                        : t("levels.steps.plan.start")}
                      <ArrowDown className="size-4" />
                    </TextButton>
                  </span>
                ),
              }}
            >
              {/* Before the first plan the guide sits open here; after, it's the header's link. */}
              {!plan && <GalleryLevelsGuideCard />}
              <div ref={plannerRef} className="scroll-mt-24">
                <GalleryLevelPlanner
                  milestones={milestones}
                  level={level}
                  onPick={planner.pickLevel}
                  limitFields={limitFields}
                  limitPlaceholders={{
                    coinBalance: t("plan.noLimit"),
                    maxTaxLoss: t("plan.noLimit"),
                    maxTransfers: t("plan.noLimit"),
                    maxSets:
                      setCount === undefined
                        ? t("plan.noLimit")
                        : grouped.format(setCount),
                  }}
                  onLimitChange={planner.setLimit}
                  customize={
                    <CustomizeButton
                      label={t("levels.customize.title")}
                      width={540}
                      active={planner.settingsChanged}
                      disabled={building}
                      panel={({ compact, close }) => (
                        <GalleryLevelSettingsPanel
                          compact={compact}
                          close={close}
                          settings={planner.settings}
                          onChange={planner.changeSettings}
                        />
                      )}
                    />
                  }
                  footer={
                    <div className="flex min-w-0 flex-1 flex-col gap-3">
                      {/* The first owners update, above the build it improves, with its rough duration. */}
                      <span className="flex flex-wrap items-center gap-x-2 gap-y-1 text-small">
                        <NavLink
                          to="/fut-gallery/update-first-owners"
                          className="text-blue-400 underline"
                        >
                          {t("levels.firstOwnersLink")}
                        </NavLink>
                        {/* The update's rough duration. */}
                        {firstOwnersMinutes !== undefined && (
                          <span className="whitespace-nowrap text-neutral-500">
                            {t("levels.firstOwnersDuration", {
                              count: firstOwnersMinutes,
                            })}
                          </span>
                        )}
                      </span>
                      {/* Left: the action; right: what a build does, or the build's status. Stacked on phones. */}
                      <div className="flex flex-col gap-3 mobile:flex-row mobile:items-center">
                        <span className="flex w-full items-center gap-x-2 mobile:w-auto">
                          {planReady ? (
                            <CompletedChip
                              size="medium"
                              // Phones: full width, like the button it stands in for.
                              className={cn(isMobile && "w-full")}
                            >
                              {t("levels.planReady")}
                            </CompletedChip>
                          ) : (
                            <Button
                              variant="credit-magic"
                              size="medium"
                              // Phones: full width over the hint.
                              stretch={isMobile}
                              disabled={level === undefined}
                              loading={building}
                              onClick={(e) => {
                                // A build needs a user. Stopped, else the modal's outside-click listener catches this same click.
                                if (!isLoggedIn) {
                                  e.stopPropagation();
                                  dispatch(
                                    showModal({ modalType: "login-required" }),
                                  );
                                  return;
                                }
                                planner.buildPlan();
                              }}
                            >
                              {/* Phones: the price hangs off the centred label inside the button, like the SBC page's. */}
                              <span className="relative flex items-center">
                                {isMobile && (
                                  <CreditCostMarker
                                    featureId="gallery-level-plan-built"
                                    placement="beside-button"
                                  />
                                )}
                                <span
                                  className={cn(
                                    level !== undefined && "text-holo",
                                    building && "animate-pulse",
                                  )}
                                >
                                  {t(
                                    `levels.${planner.buildLabel.key}`,
                                    planner.buildLabel.values,
                                  )}
                                </span>
                              </span>
                            </Button>
                          )}
                          {!isMobile && !planReady && (
                            <CreditCostMarker featureId="gallery-level-plan-built" />
                          )}
                        </span>
                        {/* The hint until the first plan; after it, only an error. */}
                        {(buildStatus || !plan) && (
                          <span className="min-w-0 flex-1 text-small text-neutral-400">
                            {buildStatus || t("levels.planner.hint")}
                          </span>
                        )}
                      </div>
                    </div>
                  }
                  open={plannerOpen ?? !plan}
                  onToggle={() => setPlannerOpen(!(plannerOpen ?? !plan))}
                />
              </div>
            </GallerySteps>
          </>
        )}
      </div>
      {ready && plan && (
        <>
          <div className="mx-auto flex w-full max-w-content flex-col px-4 pb-12 pt-8 large-desktop:px-8">
            <GalleryLevelPlanDetail
              // A rebuilt plan starts with its own first set open.
              key={plan.computedAt}
              plan={plan}
              setItems={setItems}
              loadSetItems={loadSetItems}
              bought={bought}
              onToggleBought={toggleBought}
              onToggleDone={toggleSetDone}
              summary={
                <GalleryLevelPlanSummary
                  plan={plan}
                  targetScore={planTarget}
                  coinBalance={plan.coinBalance ?? coins}
                />
              }
            />
          </div>
        </>
      )}
      <GalleryLevelsGuideSheet
        plan={plan}
        isOpen={guideOpen}
        onClose={() => setGuideOpen(false)}
      />
      {sync.ui}
    </main>
  );
};

export default GalleryLevelsPage;
